import { useNavigate } from "react-router-dom";
import { setIntent } from "@/lib/onboardingIntent";
import {
  Home,
  Search,
  Heart,
  User,
  MessageSquare,
  Building2,
  Shield,
  LogIn,
  LogOut,
  Map,
  Info,
  PlusSquare,
  ExternalLink,
  Inbox,
  Megaphone,
  ClipboardList,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useUserTypes } from "@/hooks/useUserTypes";
import { useProperties } from "@/hooks/useProperties";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Global ⌘K / Ctrl+K command palette. Mounted at the app root in App.tsx.
 *
 * Surfaces:
 *  - Navigation actions (Home, Browse, Profile, Favorites, Inbox, Areas, etc.)
 *  - Auth actions (Sign in / Sign out / List your home)
 *  - Admin entries when the user has the admin role
 *  - Live property search, fuzzy matches on title / location, jumps to the
 *    property detail page on select
 */
const CommandPalette = ({ open, onOpenChange }: CommandPaletteProps) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: isAdmin = false } = useIsAdmin();
  const { isOwner } = useUserTypes();
  // The palette is mounted on every page but opened on few. Until it opens it
  // only reads whatever listing list another page already cached; opening it
  // on a page without one (a legal page, a listing) fetches the list then.
  const { data: properties = [] } = useProperties({ enabled: open });

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const externalGo = (path: string) => {
    onOpenChange(false);
    window.open(path, "_blank", "noopener,noreferrer");
  };

  // Limit shown properties to keep the list scannable, cmdk fuzzy-filters
  // them as the user types, so 25 cached entries is plenty for any query.
  const visibleProperties = properties.slice(0, 25);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Type a command or search listings…" />
      <CommandList className="max-h-[50dvh] pb-2">
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Navigate">
          <CommandItem onSelect={() => go("/")}>
            <Home /> <span>Home</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/properties")}>
            <Search /> <span>Browse rentals</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/areas")}>
            <Map /> <span>Explore areas</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/about")}>
            <Info /> <span>About & contact</span>
          </CommandItem>
          {user && (
            <>
              <CommandItem onSelect={() => go("/favorites")}>
                <Heart /> <span>Saved properties</span>
              </CommandItem>
              <CommandItem onSelect={() => go("/messages")}>
                <MessageSquare /> <span>Messages</span>
              </CommandItem>
              <CommandItem onSelect={() => go("/profile")}>
                <User /> <span>My profile</span>
              </CommandItem>
              {isOwner && (
                <CommandItem onSelect={() => go("/dashboard")}>
                  <Building2 /> <span>My properties</span>
                </CommandItem>
              )}
            </>
          )}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Actions">
          {!user && (
            <CommandItem onSelect={() => go("/login")}>
              <LogIn /> <span>Sign in / Sign up</span>
            </CommandItem>
          )}
          <CommandItem
            onSelect={() => {
              setIntent("owner");
              go(user ? "/list-property/new" : "/owner-onboarding");
            }}
          >
            <PlusSquare /> <span>List your home</span>
          </CommandItem>
          {user && (
            <CommandItem
              onSelect={() => {
                onOpenChange(false);
                signOut();
              }}
            >
              <LogOut /> <span>Sign out</span>
            </CommandItem>
          )}
        </CommandGroup>

        {isAdmin && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Admin">
              <CommandItem onSelect={() => go("/admin?tab=overview")}>
                <Shield /> <span>Admin overview</span>
              </CommandItem>
              <CommandItem onSelect={() => go("/admin?tab=inbox")}>
                <Inbox /> <span>Admin inbox</span>
              </CommandItem>
              <CommandItem onSelect={() => go("/admin?tab=listings")}>
                <Building2 /> <span>Admin listings</span>
              </CommandItem>
              <CommandItem onSelect={() => go("/admin?tab=users")}>
                <User /> <span>Admin users</span>
              </CommandItem>
              <CommandItem onSelect={() => go("/admin/product-backlog")}>
                <ClipboardList /> <span>Product backlog</span>
              </CommandItem>
              <CommandItem onSelect={() => go("/admin/posters/msth-launch")}>
                <Megaphone /> <span>Brand poster</span>
              </CommandItem>
              <CommandItem onSelect={() => go("/admin/posters/listing-share")}>
                <Megaphone /> <span>Listing-share poster</span>
              </CommandItem>
              <CommandItem onSelect={() => externalGo("/")}>
                <ExternalLink /> <span>View site (new tab)</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}

        {visibleProperties.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Properties">
              {visibleProperties.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`${p.title} ${p.location} ${p.slug}`}
                  onSelect={() => go(`/properties/${p.slug}`)}
                >
                  <Building2 />
                  <span className="truncate">{p.title}</span>
                  <span className="ml-auto text-xs text-muted-foreground truncate">
                    {p.location}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
